import * as React from "react";
import { TeamMemberWithDetails } from "../../../types/team_members.types";
import { RegisterTeamMemberDto } from "../../../types/register-team-member.types";
import { EditableField } from "../../../components/editable-field";
import { BLOOD_TYPE_OPTIONS } from "../../../constants/blood-type-select-options";
import { GENDER_OPTIONS } from "../../../constants/gender-select-options";
import { DateField } from "../../../components/date-field";
import { AddressForm } from "../../../components/address-form";
import { Button } from "../../../components/button";
import { useToast } from "../../../components/toast";
import { PersonalInfoApiError } from "../../../types/personal_info.types";

export interface ITeamMemberFormBaseProps {
  title?: string;
  buttonText: string;
  buttonVariant?: "create" | "save";
  handleChange: (e: React.ChangeEvent<HTMLElement>) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  personalInfoApiError: PersonalInfoApiError;
}
export interface ITeamMemberFormEditProps extends ITeamMemberFormBaseProps {
  teamMember: TeamMemberWithDetails;
  createTeamMember?: never;
}
export interface ITeamMemberFormCreateProps extends ITeamMemberFormBaseProps {
  teamMember?: never;
  createTeamMember: RegisterTeamMemberDto;
}
export type ITeamMemberFormProps =
  | ITeamMemberFormEditProps
  | ITeamMemberFormCreateProps;

export function TeamMemberForm({
  title,
  buttonText,
  buttonVariant,
  teamMember,
  createTeamMember,
  handleChange,
  onSubmit,
  onCancel,
  isSubmitting,
  personalInfoApiError,
}: ITeamMemberFormProps) {
  const { addToast } = useToast();

  const currentTeamMember = teamMember ?? createTeamMember;

  const isHelmsman = currentTeamMember.is_helmsman ?? false;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isHelmsman && !currentTeamMember.personal_info?.licence_number) {
      addToast({
        message: "Se necesita licencia para ser timonel",
        type: "error",
        location: "bottom-center",
        duration: 10000,
      });
      return;
    }

    onSubmit(e);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex space-x-2">
        {title && (
          <h2 className="text-2xl font-bold text-center text-gray-600">
            {title}
          </h2>
        )}
      </div>
      <div className="space-y-4">
        <EditableField
          label="DNI"
          value={currentTeamMember.personal_info?.dni || ""}
          onChange={handleChange}
          required
          type="text"
          name="personal_info.dni"
          minLength={4}
          maxLength={50}
          placeholder="Ingrese su DNI"
          customError={personalInfoApiError?.dni ?? ""}
          apiError={personalInfoApiError?.dni !== null ? "conflict" : undefined}
        />
        <EditableField
          label="Nombre"
          value={currentTeamMember.personal_info?.first_name || ""}
          onChange={handleChange}
          required
          minLength={3}
          maxLength={100}
          type="text"
          name="personal_info.first_name"
          placeholder="Ingrese su nombre"
        />
        <EditableField
          label="Apellido"
          value={currentTeamMember.personal_info?.last_name || ""}
          onChange={handleChange}
          required
          minLength={3}
          maxLength={100}
          type="text"
          name="personal_info.last_name"
          placeholder="Ingrese su apellido"
        />
        <EditableField
          label="Timonel"
          type="checkbox"
          value={currentTeamMember.is_helmsman ?? false}
          onChange={handleChange}
          name="is_helmsman"
        />
        <EditableField
          label="Número de Licencia"
          value={currentTeamMember.personal_info?.licence_number || ""}
          onChange={handleChange}
          name="personal_info.licence_number"
          type="text"
          minLength={4}
          maxLength={50}
          placeholder="Ingrese su número de licencia"
          {...(isHelmsman ? { required: true } : {})}
          customError={personalInfoApiError?.licence_number ?? ""}
          apiError={personalInfoApiError?.licence_number !== null ? "conflict" : undefined}
        />
        <EditableField
          label="Teléfono"
          value={currentTeamMember.personal_info?.primary_phone_number || ""}
          onChange={handleChange}
          type="text"
          minLength={8}
          maxLength={15}
          required
          name="personal_info.primary_phone_number"
          placeholder="Ingrese su número de teléfono"
        />
        <EditableField
          label="Correo Electrónico"
          value={currentTeamMember.personal_info?.email || ""}
          onChange={handleChange}
          type="email"
          minLength={8}
          maxLength={100}
          name="personal_info.email"
          placeholder="Ingrese su correo electrónico"
          customError={personalInfoApiError?.email ?? ""}
          apiError={personalInfoApiError?.email !== null ? "conflict" : undefined}
        />

        <DateField
          label="Fecha de Nacimiento"
          value={
            currentTeamMember.personal_info?.birth_date
              ? new Date(currentTeamMember.personal_info.birth_date)
                  .toISOString()
                  .split("T")[0]
              : ""
          }
          onChange={handleChange}
          name="personal_info.birth_date"
          type="date"
          required
        />

        <EditableField
          label="Contacto de Emergencia"  
          value={
            currentTeamMember.personal_info?.emergency_contact_number || ""
          }
          onChange={handleChange}
          name="personal_info.emergency_contact_number"
          type="text"
          minLength={8}
          maxLength={15}
          placeholder="Ingrese el número de teléfono de su contacto de emergencia"
        />
        <EditableField
          label="Género"
          value={currentTeamMember.personal_info?.gender || ""}
          options={GENDER_OPTIONS}
          type="select"
          onChange={handleChange}
          name="personal_info.gender"
          placeholder="Seleccione su género"
          required
        />
        <EditableField
          label="Grupo Sanguíneo"
          value={currentTeamMember.personal_info?.blood_type || ""}
          options={BLOOD_TYPE_OPTIONS}
          type="select"
          onChange={handleChange}
          name="personal_info.blood_type"
          placeholder="Seleccione su grupo sanguíneo"
        />
        <h4 className="text-lg font-bold text-gray-800 mb-2">Dirección</h4>
        <AddressForm
          addresses={currentTeamMember.addresses!}
          onChange={handleChange}
        />
      </div>
      <div className="flex justify-end space-x-2 mt-8">
        <Button onClick={() => onCancel()} text="Cancelar" variant="cancel" />
        <Button
          type="submit"
          text={buttonText}
          variant={buttonVariant || "create"}
          disabled={isSubmitting}
        />
      </div>
    </form>
  );
}
